import { DestroyRef, Injectable, InjectionToken, computed, effect, inject, signal, untracked } from '@angular/core';

import { AuthService } from '../auth/auth.service';
import { injectAppTvMode } from '../../shared/tv-mode';
import { environment } from '../../../environments/environment';
import type { AlertLevel, OperationalAlert } from '../../models/operational-alert.model';

// El stream SSE exige el JWT en el header Authorization y EventSource nativo no puede mandar
// headers, así que se consume con fetch crudo + ReadableStream. Fetch crudo también significa
// que los interceptors de HttpClient NO aplican: el Bearer se agrega a mano acá, y el timeout
// de 30s del timeoutInterceptor tampoco corre — deseable, la conexión es larga por diseño.
// Token de DI para que los tests fakeen el stream sin red real.
export const ALERT_STREAM_FETCH = new InjectionToken<typeof fetch>('ALERT_STREAM_FETCH', {
  providedIn: 'root',
  factory: () => window.fetch.bind(window),
});

export type AlertStreamConnectionState = 'connected' | 'reconnecting' | 'disconnected';

// Buffer de sesión, no historial: el backend todavía no persiste alertas, así que solo se
// retiene lo que llegó con el panel abierto. Cap para que una integración inestable durante
// horas no acumule memoria sin límite.
const MAX_BUFFERED_ALERTS = 100;
const INITIAL_RECONNECT_DELAY_MS = 5_000;
const MAX_RECONNECT_DELAY_MS = 30_000;

const ALERT_LEVELS: readonly string[] = ['INFO', 'WARNING', 'CRITICAL'] satisfies readonly AlertLevel[];

// Validación estructural del payload, no solo JSON.parse: un frame con forma inesperada se
// descarta sin romper el stream ni meter basura en la UI.
function toOperationalAlert(payload: unknown): OperationalAlert | null {
  if (typeof payload !== 'object' || payload === null) {
    return null;
  }
  const candidate = payload as Record<string, unknown>;
  const { level, source, code, message, reference, occurredAt } = candidate;
  if (typeof level !== 'string' || !ALERT_LEVELS.includes(level)) {
    return null;
  }
  if (typeof source !== 'string' || typeof code !== 'string' || typeof message !== 'string') {
    return null;
  }
  if (typeof occurredAt !== 'string') {
    return null;
  }
  return {
    level: level as AlertLevel,
    source,
    code,
    message,
    reference: typeof reference === 'string' ? reference : null,
    occurredAt,
  };
}

@Injectable({ providedIn: 'root' })
export class AlertStreamService {
  private readonly auth = inject(AuthService);
  // QA 2026-10: not in TV mode either (an ADMIN previewing ?modo=tv shows nothing that uses it),
  // nor before TV mode is known (first navigation); leaving TV mode turns the token on.
  private readonly tvMode = injectAppTvMode();
  private readonly sessionToken = computed(() =>
    this.tvMode() === false ? this.auth.opsSessionToken() : null,
  );
  private readonly fetchImpl = inject(ALERT_STREAM_FETCH);

  private readonly _alerts = signal<OperationalAlert[]>([]);
  private readonly _unseenCount = signal(0);
  private readonly _connectionState = signal<AlertStreamConnectionState>('disconnected');

  /** Alertas de la sesión, la más nueva primero (máx. MAX_BUFFERED_ALERTS). */
  readonly alerts = this._alerts.asReadonly();
  readonly unseenCount = this._unseenCount.asReadonly();
  readonly connectionState = this._connectionState.asReadonly();

  private controller: AbortController | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelayMs = INITIAL_RECONNECT_DELAY_MS;

  constructor() {
    // Reacciona al token (no solo al booleano de sesión): un re-login trae token nuevo y debe
    // reconectar con esa credencial. untracked: la conexión solo depende del estado de auth,
    // no de las señales que ella misma escribe. Only OPS sessions: /admin/events/** is OPS tier,
    // a METRICS_VIEWER got a 403 retried forever by the reconnect loop.
    effect(() => {
      const token = this.sessionToken();
      untracked(() => (token !== null ? this.start() : this.stop()));
    });
    inject(DestroyRef).onDestroy(() => this.stop());
  }

  markAllSeen(): void {
    this._unseenCount.set(0);
  }

  private start(): void {
    this.teardown();
    this.reconnectDelayMs = INITIAL_RECONNECT_DELAY_MS;
    this.controller = new AbortController();
    this._connectionState.set('reconnecting');
    void this.connect(this.controller.signal);
  }

  private stop(): void {
    this.teardown();
    this._connectionState.set('disconnected');
  }

  private teardown(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.controller?.abort();
    this.controller = null;
  }

  private async connect(signal: AbortSignal): Promise<void> {
    try {
      const token = untracked(this.auth.token);
      if (token === null) {
        return;
      }
      const response = await this.fetchImpl(`${environment.apiUrl}/admin/events/stream`, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
        signal,
      });
      if (signal.aborted) {
        return;
      }
      if (response.status === 401) {
        // Credencial rechazada: reintentar con el mismo token sería loopear 401s contra el
        // backend. Se corta acá; el próximo cambio de estado de auth (re-login) reconecta.
        this.stop();
        return;
      }
      if (!response.ok || response.body === null) {
        throw new Error(`alert stream HTTP ${response.status}`);
      }
      this._connectionState.set('connected');
      this.reconnectDelayMs = INITIAL_RECONNECT_DELAY_MS;
      await this.readStream(response.body, signal);
      if (!signal.aborted) {
        // El servidor cerró la respuesta (deploy, timeout de proxy): mismo camino que un corte.
        this.scheduleReconnect(signal);
      }
    } catch {
      if (!signal.aborted) {
        this.scheduleReconnect(signal);
      }
    }
  }

  private scheduleReconnect(signal: AbortSignal): void {
    this._connectionState.set('reconnecting');
    const delay = this.reconnectDelayMs;
    this.reconnectDelayMs = Math.min(this.reconnectDelayMs * 2, MAX_RECONNECT_DELAY_MS);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!signal.aborted) {
        void this.connect(signal);
      }
    }, delay);
  }

  // Parser SSE mínimo (el subset que emite el backend): comentarios (líneas ":..."), campos
  // event:/data: y frames separados por línea en blanco. Los chunks del wire no respetan los
  // límites de frame, por eso se acumula en buffer y se corta por líneas completas.
  private async readStream(body: ReadableStream<Uint8Array>, signal: AbortSignal): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let eventName = '';
    let dataLines: string[] = [];

    for (;;) {
      const { done, value } = await reader.read();
      // El chequeo de aborted va antes de procesar: un chunk que quedó en vuelo al cerrar
      // sesión no debe filtrar alertas a un estado ya desconectado.
      if (signal.aborted || done) {
        return;
      }
      buffer += decoder.decode(value, { stream: true });

      let newlineIndex: number;
      while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, newlineIndex).replace(/\r$/, '');
        buffer = buffer.slice(newlineIndex + 1);

        if (line === '') {
          this.dispatchFrame(eventName, dataLines);
          eventName = '';
          dataLines = [];
        } else if (line.startsWith(':')) {
          // Comentarios SSE (":connected", ":keepalive"): protocolo, no datos.
        } else {
          const separator = line.indexOf(':');
          const field = separator === -1 ? line : line.slice(0, separator);
          const fieldValue = separator === -1 ? '' : line.slice(separator + 1).replace(/^ /, '');
          if (field === 'event') {
            eventName = fieldValue;
          } else if (field === 'data') {
            dataLines.push(fieldValue);
          }
        }
      }
    }
  }

  private dispatchFrame(eventName: string, dataLines: string[]): void {
    if (eventName !== 'alert' || dataLines.length === 0) {
      return;
    }
    let payload: unknown;
    try {
      payload = JSON.parse(dataLines.join('\n'));
    } catch {
      // Frame malformado: se ignora sin matar el stream — la próxima alerta válida entra igual.
      return;
    }
    const alert = toOperationalAlert(payload);
    if (alert === null) {
      return;
    }
    this._alerts.update((list) => [alert, ...list].slice(0, MAX_BUFFERED_ALERTS));
    this._unseenCount.update((count) => count + 1);
  }
}
